// RealEstateNavbar.jsx (updated)
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { Avatar } from "@chakra-ui/react";
import { 
  Home, 
  Search, 
  Key, 
  TrendingUp, 
  Building2, 
  PlusCircle, 
  User,
  LayoutDashboard,
  Hammer,
  BookOpen
} from "lucide-react";
import logo from "../../images/logo.png";

const RealEstateNavbar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setUser(user);
    });

    // Set active tab based on current route
    const path = location.pathname;
    if (path.includes("/category/sale")) setActiveTab("buy");
    else if (path.includes("/category/rent")) setActiveTab("rent");
    else if (path.includes("/create-listing")) setActiveTab("sell");
    else if (path.includes("/feed")) setActiveTab("feed");
    else if (path.includes("/invest")) setActiveTab("invest");
    else if (path.includes("/blogs")) setActiveTab("blogs");
    else if (path.includes("/projects")) setActiveTab("projects");
    else if (path === "/") setActiveTab("home");

    return () => unsubscribe();
  }, [location.pathname, auth]);

  const handleTabClick = (tabName, path) => {
    setActiveTab(tabName);
    if (path) navigate(path);
  };

  const getUserName = () => {
    if (user?.displayName) return user.displayName;
    if (user?.email) return user.email.split('@')[0];
    return "User";
  };

  // Bottom navigation items for mobile
  const bottomNavItems = [
    { id: "home", label: "Home", icon: Home, path: "/" },
    { id: "feed", label: "Feed", icon: LayoutDashboard, path: "/feed" },
    { id: "buy", label: "Buy", icon: Search, path: "/category/sale" },
    { id: "rent", label: "Rent", icon: Key, path: "/category/rent" },
    { id: "projects", label: "Projects", icon: Building2, path: "/projects" },
  ];

  const NavTabs = () => (
    <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-1">
      <Link
        to="/feed"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "feed"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("feed")}
      >
        Buyer Requests
      </Link>

      <Link
        to="/"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "home"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("home")}
      >
        Home
      </Link>

      <Link
        to="/category/sale"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "buy"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("buy")}
      >
        Buy
      </Link>

      <Link
        to="/category/rent"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "rent"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("rent")}
      >
        Rent
      </Link>

      <Link
        to="/projects"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "projects"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("projects")}
      >
        Projects
      </Link>

      <Link
        to="/blogs"
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === "blogs"
            ? "bg-green-50 text-green-600 border-b-2 border-green-600"
            : "text-gray-600 hover:text-green-500 hover:bg-gray-50"
        }`}
        onClick={() => handleTabClick("blogs")}
      >
        Blogs
      </Link>

      <Link
        to="/create-listing"
        className={`px-4 py-2 rounded-lg font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2 ${
          activeTab === "sell" ? "ring-2 ring-green-300 ring-opacity-50" : ""
        }`}
        onClick={() => handleTabClick("sell")}
      >
        Sell Property
      </Link>
    </div>
  );

  const AuthButtons = () => (
    <div className="flex flex-col sm:flex-row gap-2">
      <Link
        to="/sign-in"
        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:border-green-400 hover:bg-green-50 transition-all duration-200 text-center min-w-[100px]"
        onClick={() => handleTabClick("signin")}
      >
        Sign In
      </Link>
      <Link
        to="/sign-up"
        className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-600 to-green-700 text-white font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg text-center min-w-[100px]"
        onClick={() => handleTabClick("signup")}
      >
        Sign Up
      </Link>
    </div>
  );

  const UserAvatar = () => (
    <Link
      to="/profile"
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-green-50 transition-all duration-200 group"
      onClick={() => handleTabClick("profile")}
    >
      <div className="hidden md:flex flex-col items-end">
        <span className="text-sm font-medium text-gray-700 group-hover:text-green-600">
          {getUserName()}
        </span>
        <span className="text-xs text-gray-500">View Profile</span>
      </div>
      <Avatar 
        name={user?.displayName || user?.email || "User"}
        src={user?.photoURL || ""}
        size="sm"
        bg="green.500"
        color="white"
        className="ring-2 ring-green-200 group-hover:ring-green-300 transition-all duration-200"
      />
    </Link>
  );

  // Mobile Bottom Navigation Bar
  const MobileBottomNav = () => (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 safe-area-bottom">
      <div className="flex justify-around items-center px-2 py-2 max-w-screen mx-auto">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleTabClick(item.id, item.path)}
              className={`flex flex-col items-center justify-center flex-1 py-1 rounded-lg transition-all duration-200 bottom-nav-button ${
                isActive
                  ? "text-green-600"
                  : "text-gray-500 hover:text-green-500"
              }`}
            >
              <Icon 
                size={20} 
                className={isActive ? "animate-pulse" : ""}
                strokeWidth={isActive ? 2.5 : 2}
              />
              <span className={`text-[10px] sm:text-xs mt-1 font-medium ${
                isActive ? "text-green-600 font-semibold" : "text-gray-600"
              }`}>
                {item.label}
              </span>
              {isActive && (
                <div className="absolute -top-2 w-6 h-0.5 bg-green-500 rounded-full" />
              )}
            </button>
          );
        })}
        
        {/* Blogs Button */}
        <button
          onClick={() => handleTabClick("blogs", "/blogs")}
          className={`flex flex-col items-center justify-center flex-1 py-1 rounded-lg transition-all duration-200 bottom-nav-button ${
            activeTab === "blogs"
              ? "text-green-600"
              : "text-gray-500 hover:text-green-500"
          }`}
        >
          <div className="relative">
            <BookOpen 
              size={20} 
              className={activeTab === "blogs" ? "text-green-600" : "text-gray-500"}
              strokeWidth={2}
            />
            {activeTab === "blogs" && (
              <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
            )}
          </div>
          <span className={`text-[10px] sm:text-xs mt-1 font-medium ${
            activeTab === "blogs" ? "text-green-600 font-semibold" : "text-gray-600"
          }`}>
            Blogs
          </span>
        </button>

        {/* Sell Property Button */}
        <button
          onClick={() => handleTabClick("sell", "/create-listing")}
          className={`flex flex-col items-center justify-center flex-1 py-1 rounded-lg transition-all duration-200 bottom-nav-button ${
            activeTab === "sell"
              ? "text-green-600"
              : "text-gray-500 hover:text-green-500"
          }`}
        >
          <div className="relative">
            <PlusCircle 
              size={20} 
              className={activeTab === "sell" ? "text-green-600" : "text-gray-500"}
              strokeWidth={2}
            />
            {activeTab === "sell" && (
              <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
            )}
          </div>
          <span className={`text-[10px] sm:text-xs mt-1 font-medium ${
            activeTab === "sell" ? "text-green-600 font-semibold" : "text-gray-600"
          }`}>
            Sell
          </span>
        </button>

        {/* Profile/Sign In Button */}
        {isLoggedIn ? (
          <Link
            to="/profile"
            onClick={() => handleTabClick("profile")}
            className={`flex flex-col items-center justify-center flex-1 py-1 rounded-lg transition-all duration-200 bottom-nav-button ${
              activeTab === "profile"
                ? "text-green-600"
                : "text-gray-500 hover:text-green-500"
            }`}
          >
            <User size={20} strokeWidth={activeTab === "profile" ? 2.5 : 2} />
            <span className="text-[10px] sm:text-xs mt-1 font-medium">Profile</span>
            {activeTab === "profile" && (
              <div className="absolute -top-2 w-6 h-0.5 bg-green-500 rounded-full" />
            )}
          </Link>
        ) : (
          <button
            onClick={() => handleTabClick("signin", "/sign-in")}
            className={`flex flex-col items-center justify-center flex-1 py-1 rounded-lg transition-all duration-200 bottom-nav-button ${
              activeTab === "signin"
                ? "text-green-600"
                : "text-gray-500 hover:text-green-500"
            }`}
          >
            <User size={20} strokeWidth={activeTab === "signin" ? 2.5 : 2} />
            <span className="text-[10px] sm:text-xs mt-1 font-medium">Login</span>
            {activeTab === "signin" && (
              <div className="absolute -top-2 w-6 h-0.5 bg-green-500 rounded-full" />
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-3"
              onClick={() => handleTabClick("home")}
            >
              <img
                src={logo}
                alt="EstateGhar Logo"
                className="h-[40px] sm:h-[45px] lg:h-[50px] w-full object-contain"
              />
            </Link>

            {/* Desktop Navigation Tabs */}
            <div className="hidden lg:flex items-center space-x-1">
              <NavTabs />
            </div>

            {/* Desktop Auth/User Section */}
            <div className="hidden lg:flex items-center">
              {isLoggedIn ? <UserAvatar /> : <AuthButtons />}
            </div>

            {/* Mobile - Only show avatar, no hamburger menu */}
            <div className="lg:hidden flex items-center">
              {isLoggedIn ? (
                <Link
                  to="/profile"
                  onClick={() => handleTabClick("profile")}
                  className="flex items-center"
                >
                  <Avatar 
                    name={user?.displayName || user?.email || "User"}
                    src={user?.photoURL || ""}
                    size="sm"
                    bg="green.500"
                    color="white"
                    className="ring-2 ring-green-200 hover:ring-green-300 transition-all duration-200"
                  />
                </Link>
              ) : (
                <div className="flex gap-2">
                  <Link
                    to="/sign-in"
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 font-medium hover:border-green-400 hover:bg-green-50 transition-all duration-200"
                    onClick={() => handleTabClick("signin")}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/sign-up"
                    className="px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-green-600 to-green-700 text-white font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md"
                    onClick={() => handleTabClick("signup")}
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav />

      {/* Global styles */}
      <style jsx global>{`
        /* Add padding bottom to main content to prevent overlap with bottom nav on mobile */
        @media (max-width: 1023px) {
          body {
            padding-bottom: 65px;
          }
        }
        
        /* Safe area for notched phones */
        .safe-area-bottom {
          padding-bottom: max(8px, env(safe-area-inset-bottom));
        }
        
        /* Prevent text selection on bottom nav buttons */
        .bottom-nav-button {
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Smooth transitions */
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>
    </>
  );
};

export default RealEstateNavbar;